import { GemsBadge } from "@/app/games/(details)/quest-badges";
import { getQuestIcon } from "@/app/games/(details)/types";
import {
  type DailyQuest,
  useDailyQuests,
} from "@/app/games/(details)/useDailyQuests";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { List, ListItem } from "@/components/ui/list";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PostTweetXModal } from "@/components/v2/PostTweetXModal";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { useCountdown } from "@/hooks/useCountdown";
import { cn } from "@/lib/utils";
import { Clock, Trophy } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";

// Skeleton loading component
function DailyQuestsSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <div key={`skeleton-${Date.now()}-${index}`}>
          <ListItem variant="transparent" size="md" className="px-3 py-2.5">
            <div className="flex items-start gap-4 w-full isolate">
              {/* Icon skeleton */}
              <Skeleton className="w-9 h-9 rounded border border-white/40 shrink-0" />

              {/* Content skeleton */}
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>

              {/* Rewards skeleton */}
              <div className="shrink-0 self-start">
                <Skeleton className="h-6 w-12 rounded-full" />
              </div>
            </div>
          </ListItem>

          {/* Divider */}
          <div className="flex items-center gap-5 opacity-25">
            <div className="flex-1 h-px bg-foreground opacity-15" />
          </div>
        </div>
      ))}
    </>
  );
}

// Quest list component
function DailyQuestsList({
  quests,
}: {
  quests: DailyQuest[];
}) {
  const { trackV3 } = useAnalytics();
  const [showPostTweetModalQuest, setShowPostTweetModalQuest] = useState<
    DailyQuest | undefined
  >(undefined);
  const [showConfirmation, setShowConfirmation] = useState(false);

  return (
    <>
      {quests.map((quest) => {
        const QuestIcon = getQuestIcon(quest.type, []);
        return (
          <Link
            onClick={() => {
              trackV3({
                object: "Quest",
                action: Action.PRESSED,
                extras: {
                  questId: quest.externalId,
                  questName: quest.name,
                  questType: quest.type,
                  gameId: quest.gameId,
                  section: "RightNav",
                },
              });
              if (quest.type === "post_x_tweet") {
                setShowPostTweetModalQuest(quest);
              }
            }}
            href={
              quest.type === "post_x_tweet"
                ? "#"
                : quest.linkUrl
                  ? quest.linkUrl
                  : `/games/${quest.slug}`
            }
            target={
              quest.linkUrl && quest.type !== "post_x_tweet"
                ? "_blank"
                : "_self"
            }
            key={quest.id}
          >
            <ListItem
              variant="transparent"
              size="md"
              className={cn(
                "px-3 py-2.5 hover:bg-white/5 transition-all duration-300 hover:cursor-pointer",
                quest.completedToday && "opacity-50",
              )}
            >
              <div className="flex items-start gap-4 w-full isolate">
                {/* Icon */}
                <div className="relative w-12 h-12 rounded border border-white/40 shrink-0 overflow-hidden bg-black">
                  {quest.gameIconUrl ? (
                    <Image
                      src={quest.gameIconUrl}
                      alt={quest.gameName}
                      width={48}
                      height={48}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full">
                      <QuestIcon className="w-5 h-5 text-foreground" />
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <p
                    className={cn(
                      "font-suisse font-medium text-sm leading-4 text-foreground",
                      quest.completedToday && "line-through",
                    )}
                  >
                    {quest.name}
                  </p>
                  {quest.caption && (
                    <p className="font-sans text-xs leading-4 text-muted-foreground">
                      {quest.caption}
                    </p>
                  )}
                </div>
                {quest.gems > 0 && (
                  <GemsBadge
                    gems={quest.gems}
                    completed={quest.completedToday}
                    className="bg-neutral-950/40 self-start"
                  />
                )}
              </div>
            </ListItem>

            {/* Divider */}
            <div className="flex items-center gap-5 opacity-25">
              <div className="flex-1 h-px bg-foreground opacity-15" />
            </div>
          </Link>
        );
      })}
      <PostTweetXModal
        visible={showPostTweetModalQuest !== undefined}
        onClose={() => {
          setShowPostTweetModalQuest(undefined);
        }}
        showConfirmationModal={() => {}}
        questDetails={{
          questRuleId: showPostTweetModalQuest?.externalId ?? "",
          questKey: "x-post-tweet-validation",
          questLink: showPostTweetModalQuest?.linkUrl ?? "",
        }}
        gameId={showPostTweetModalQuest?.gameId ?? ""}
        gameName={showPostTweetModalQuest?.gameName ?? ""}
        gameUid={showPostTweetModalQuest?.slug ?? ""}
        tweetDetails={{
          tweetText: showPostTweetModalQuest?.metadata?.tweet_text ?? "",
          tweetUsername:
            showPostTweetModalQuest?.metadata?.tweet_username ??
            showPostTweetModalQuest?.metadata?.tweet_substring ??
            "",
          tweetRequiresImage:
            showPostTweetModalQuest?.metadata?.tweet_requires_image ?? false,
        }}
        completionDetails={{
          completed: showPostTweetModalQuest?.completedToday ?? false,
          latestCompletion: undefined,
          refreshPeriodHours:
            showPostTweetModalQuest?.refreshPeriodHours ?? undefined,
          validFrom: showPostTweetModalQuest?.validFrom ?? undefined,
        }}
      />
    </>
  );
}

export function DailyQuests() {
  const { data: dailyQuests, isLoading: isDailyQuestsLoading } =
    useDailyQuests();
  const countdown = useCountdown();
  const [isOpen, setIsOpen] = useState(true);

  const allCompleted = useMemo(() => {
    const allCompleted =
      dailyQuests.length > 0 &&
      dailyQuests.every((quest) => quest.completedToday);
    if (allCompleted) {
      setIsOpen(false);
    }
    return allCompleted;
  }, [dailyQuests]);

  return (
    <div className="backdrop-blur-xl border border-white/40 rounded-lg overflow-hidden">
      <Accordion
        type="single"
        className="w-full"
        collapsible={allCompleted}
        value={isOpen ? "open" : "closed"}
        onValueChange={(value) => setIsOpen(value === "open")}
      >
        <AccordionItem value="open" className="border-0">
          <AccordionTrigger
            className="px-3 py-5 hover:no-underline"
            collapsible={allCompleted}
          >
            <div
              className={`flex items-center justify-between w-full ${allCompleted ? "mr-4" : ""}`}
            >
              <div className="flex items-start gap-2.5 flex-1">
                <Trophy className="w-3.5 h-3.5 text-foreground shrink-0" />
                <h3 className="font-suisse font-bold text-sm leading-none text-foreground">
                  Daily Tasks
                </h3>
              </div>
              <Badge
                variant="outline"
                className="bg-background border-white/10 gap-1"
                startIcon={<Clock className="w-3 h-3" />}
              >
                {countdown.hours}H : {countdown.minutes}M : {countdown.seconds}S
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-0">
            {/* Divider */}
            <div className="flex items-center gap-5 opacity-25">
              <div className="flex-1 h-px bg-foreground opacity-15" />
            </div>

            {/* Tasks */}
            <List variant="flush" size="md">
              {dailyQuests.length === 0 || isDailyQuestsLoading ? (
                <DailyQuestsSkeleton />
              ) : (
                <DailyQuestsList quests={dailyQuests} />
              )}
            </List>

            {/* Rewards Section */}
            <div className="flex flex-col gap-2.5 items-start py-2.5 px-3">
              <p className="font-sans font-semibold text-[10px] leading-normal tracking-[0.1px] text-muted-foreground">
                REWARDS
              </p>
              <div className="flex gap-2.5 items-center">
                {/* Spin Tickets Reward */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative w-14 h-14 rounded-lg border border-white/40 overflow-hidden bg-gradient-to-t from-[#6d6c6d] to-[#f3f3f3] cursor-help">
                        {/* Prize Tier Gradient Background */}

                        {/* Spin Tickets Icon */}
                        <div className="relative flex items-center justify-center w-full h-full">
                          <Image
                            src="/assets/prize-wheel/wheel-icon.png"
                            alt="Spin Tickets"
                            width={44}
                            height={44}
                            className="object-contain w-11 h-11"
                          />
                        </div>

                        {/* Amount Badge */}
                        <div className="absolute bottom-0 right-0 bg-background/50 backdrop-blur rounded-br-lg rounded-tl-lg px-1.5 py-0.25">
                          <span className="text-xs font-bold text-foreground">
                            x10
                          </span>
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>10 spins</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Bottom Divider */}
            <div className="flex items-center gap-5 opacity-25">
              <div className="flex-1 h-px bg-foreground opacity-15" />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
